import type { UserConfidence } from "@cloudox/api-client";
import { Badge } from "../primitives/index";
import { ConfidenceBadge } from "./ConfidenceBadge";

/** Where Verified/Likely/Assumed/Unknown sit relative to one another. */
const CONFIDENCE_RANK: Record<UserConfidence, number> = {
  Verified: 3,
  Likely: 2,
  Assumed: 1,
  Unknown: 0,
};

/**
 * The subset of a navigation section this component judges. Declared
 * structurally rather than as `KnowledgeSection` so a caller holding a
 * partially-known section (a static bundle, a test fixture) can still use it.
 */
export interface SectionQualifiersProps {
  readonly source: "ai" | "fallback" | "empty" | undefined;
  readonly confidence: UserConfidence | undefined;
  readonly needsImprovement?: boolean | undefined;
  readonly kqiScore?: number | null | undefined;
  /** The confidence of the view this section sits in. */
  readonly viewConfidence: UserConfidence;
  readonly className?: string;
}

/**
 * What this section is, beyond its prose: whether a model narrated it or the
 * deterministic renderer did, whether the run's own quality scoring flagged it,
 * and whether it is materially less certain than the view it sits in.
 *
 * All three are recorded by the pipeline and were being dropped. Deterministic
 * fallback prose is honest but thinner than narration, and a reader comparing
 * two sections has no way to tell which they are looking at; a section the run
 * itself scored as needing improvement should not read like the rest.
 */
export function SectionQualifiers({
  source,
  confidence,
  needsImprovement = false,
  kqiScore,
  viewConfidence,
  className = "",
}: SectionQualifiersProps) {
  const fallback = source === "fallback";
  const empty = source === "empty";
  // Only a *drop* in certainty is worth repeating. A section as certain as its
  // view adds nothing, and a badge on every section trains a reader to skip it.
  const diverges =
    confidence !== undefined &&
    CONFIDENCE_RANK[confidence] < CONFIDENCE_RANK[viewConfidence];
  if (!fallback && !empty && !needsImprovement && !diverges) return null;

  // Each qualifier says what it means in words rather than hiding it in a
  // tooltip: a hover-only explanation is unreachable by keyboard and touch, and
  // "Deterministic text" alone tells a reader nothing actionable.
  return (
    <div
      className={`mb-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs ${className}`.trim()}
    >
      {empty ? (
        <span className="inline-flex items-center gap-1.5">
          <Badge tone="neutral">No evidence to narrate</Badge>
          <span style={{ color: "var(--fg-muted)" }}>
            This run found no evidence for this section.
          </span>
        </span>
      ) : fallback ? (
        <span className="inline-flex items-center gap-1.5">
          <Badge tone="neutral">Deterministic text</Badge>
          <span style={{ color: "var(--fg-muted)" }}>
            Written by the deterministic renderer, not narrated.
          </span>
        </span>
      ) : null}
      {needsImprovement ? (
        <span className="inline-flex items-center gap-1.5">
          <Badge tone="warning">Flagged by quality review</Badge>
          <span style={{ color: "var(--fg-muted)" }}>
            {typeof kqiScore === "number"
              ? `This run scored it ${Math.round(kqiScore)}/100.`
              : "The run's own quality scoring flagged it."}
          </span>
        </span>
      ) : null}
      {diverges && confidence ? (
        <span
          className="inline-flex items-center gap-1"
          style={{ color: "var(--fg-muted)" }}
        >
          Less certain than this view overall:
          <ConfidenceBadge confidence={confidence} />
        </span>
      ) : null}
    </div>
  );
}
